import { inject, injectable } from 'inversify';

import type * as THREE from 'three';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';

import { TYPES } from '@/engine/ioc';
import { TYPES as INDICATION_TYPES } from '@/engine/app/modules/indications/iocTypes.ts';
import { CORE_TYPES } from '@/engine/app/modules/core';
import { TTVR_TYPES } from '@/surgeries/cardiac/TTVR/ioc/types.ts';

import { IntersectionKind, SceneItemKind } from '@/engine/app/interfaces.ts';
import { PointTuner } from '@/engine/PlanningAlgorithm/Tuners/PointTuner.ts';

import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { IMeasurementViewModel, Intersection, IViewPortManager } from '@/engine/app/interfaces.ts';
import type { AlgorithmHelperFactory } from '@/surgeries/cardiac/TTVR/AlgorithmHelper/AlgorithmHelper.factory.ts';
import type { ITTVRVariableDistanceAlgoHelper } from '@/surgeries/cardiac/TTVR/interfaces.ts';

import type { IIndicationsViewModelFactory } from '@/engine/app/modules/indications/interfaces.ts';
import type { ICommand, ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command.ts';
import type {
  IVariableDistanceCommandsFactory,
  VariableDistanceVMs
} from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/interfaces.ts';
import type { IIndicationController, IIndicationStateManager } from '@/components/boxes/objectBoxes/interfaces.ts';

import {
  addVariableDistancesViewModels,
  removeVariableDistancesViewModels
} from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/VariableDistancesViewModelsHelper.ts';
import { buildLeafletCommonAlgorithm } from '@/surgeries/cardiac/TTVR/TTVRVariableDistanceAlgorithmBuilder.ts';
import { TTVRPlanningEntityKind } from '@/surgeries/cardiac/TTVR/TTVRPlanningEntityKind.ts';
import type { IndicationColorSetter } from '@/components/boxes/IndicationColorSetter';
import { neonLemonColor } from '@/surgeries/cardiac/TTVR/configs/objects.config';

@injectable()
export class LeafletsIndicationController implements IIndicationController {
  private readonly _cuspPointColor = neonLemonColor;

  private readonly _availableMeasurementsName = 'available_leaflet';

  private readonly _extensionDescriptor = 'leafletCommonAlgorithm';

  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _indicationColorSetter: IndicationColorSetter;

  private _viewModels: VariableDistanceVMs | undefined;

  private _viewModelsAdded = false;

  private _descriptor: string | undefined;

  private readonly _leafletAlgoHelper: ITTVRVariableDistanceAlgoHelper;

  private readonly _vmFactory: IIndicationsViewModelFactory;

  private _stateManager: IIndicationStateManager | undefined;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _variableDistanceCommandsFactory: IVariableDistanceCommandsFactory;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(INDICATION_TYPES.IIndicationsViewModelFactory) vmFactory: IIndicationsViewModelFactory,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor,
    @inject(TYPES.VariableDistanceCommandsFactory) variableDistanceCommandsFactory: IVariableDistanceCommandsFactory,
    @inject(TTVR_TYPES.AlgorithmHelperFactory) algorithmHelperFactory: AlgorithmHelperFactory,
    @inject(TYPES.IndicationColorSetter) indicationColorSetter: IndicationColorSetter
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._indicationColorSetter = indicationColorSetter;
    this._vmFactory = vmFactory;
    this._leafletAlgoHelper = algorithmHelperFactory.createVariableDistanceAlgorithmHelper(
      this._planningAlgorithm,
      this._availableMeasurementsName,
      this._extensionDescriptor,
      buildLeafletCommonAlgorithm
    );
    this._commandProcessor = commandProcessor;
    this._variableDistanceCommandsFactory = variableDistanceCommandsFactory;
  }

  public setStateManager(stateManager: IIndicationStateManager): void {
    this._stateManager = stateManager;
  }

  public async initializeIndication(descriptor: string, label: string): Promise<void> {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }

    const pointDescriptor: string = 'tp.pt_' + descriptor;

    const pointViewModel = this._vmFactory.createViewModel(
      SceneItemKind.VariableDistancePoint,
      undefined,
      pointDescriptor,
      [descriptor]
    );

    this._indicationColorSetter.setIndicationPointColor(pointViewModel, this._cuspPointColor);

    const distanceViewModel = this._vmFactory.createMeasurementViewModel(
      SceneItemKind.LabeledDistanceMeasurement,
      undefined,
      descriptor,
      [pointDescriptor],
      label,
      0.2,
      undefined,
      {
        boxGroup: TTVRPlanningEntityKind.Leaflet
      }
    );

    this._viewModels = {
      pointViewModel: pointViewModel,
      distanceViewModel: distanceViewModel
    };

    this._descriptor = descriptor;

    await this._leafletAlgoHelper.addMeasurementAsync(descriptor);
  }

  public async indicate(vpm: IViewPortManager, screenPoint: THREE.Vector2): Promise<boolean> {
    const raycasted: Intersection | undefined = vpm
      .getRaycaster()
      .rayCast(screenPoint, (intersection: Intersection): boolean => intersection.kind !== IntersectionKind.Point);
    if (!raycasted) {
      return false;
    }
    if (!this._viewModels) {
      console.warn('ViewModels have to be defined');
      return false;
    }
    const pointTuner = new PointTuner(this._planningAlgorithm);
    await pointTuner.setValueAsync(this._viewModels.pointViewModel.descriptor, raycasted.point);
    if (!this._viewModelsAdded) {
      this._addViewModels(this._viewModels);
    }
    return true;
  }

  private _addViewModels(viewModels: VariableDistanceVMs): void {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }
    addVariableDistancesViewModels(
      currentView,
      this._viewsService,
      viewModels.distanceViewModel,
      viewModels.pointViewModel
    );
    this._viewModelsAdded = true;
  }

  private _removeViewModels(viewModels: VariableDistanceVMs): void {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }
    removeVariableDistancesViewModels(
      currentView,
      this._viewsService,
      viewModels.distanceViewModel,
      viewModels.pointViewModel
    );
    this._viewModelsAdded = false;
  }

  public suspendIndication(): void {
    if (!this._viewModels) {
      console.warn('ViewModels have to be defined');
      return;
    }
    this._removeViewModels(this._viewModels);
  }

  private _cleanup(): void {
    this._viewModels = undefined;
    this._viewModelsAdded = false;
    this._descriptor = undefined;
    this._stateManager?.setIndicationState(false);
  }

  public async finalizeIndication(vpm: IViewPortManager, screenPoint: THREE.Vector2): Promise<boolean> {
    const raycasted = vpm.getRaycaster().rayCast(screenPoint);
    if (!raycasted) {
      return false;
    }
    if (!this._viewModels || !this._viewModelsAdded) {
      return false;
    }
    const pointViewModel = this._viewModels.pointViewModel.clone();
    const distanceViewModel = this._viewModels.distanceViewModel.clone() as IMeasurementViewModel;
    const currentView = this._viewsService.currentViewOrThrow;
    const point = raycasted.point;

    await this.cancelIndication();

    const command: ICommand = this._variableDistanceCommandsFactory.createAddVariableDistanceCommand(
      currentView,
      point,
      pointViewModel,
      distanceViewModel,
      this._leafletAlgoHelper
    );
    await this._commandProcessor.execute(command);

    this._cleanup();
    return true;
  }

  public async removeIndicated(descriptor: string): Promise<void> {
    const currentView = this._viewsService.currentViewOrThrow;
    const distanceViewModel = currentView.getMeasurements().find((m) => m.descriptor === descriptor);
    const pointViewModel = currentView.getObjects().find((m) => m.descriptor === distanceViewModel?.attachments[0]);

    if (!distanceViewModel || !pointViewModel) {
      console.warn('Can not find distance measurement or cusp point');
      return;
    }

    const pointModel = await this._planningAlgorithm.getValueAsync(pointViewModel.descriptor, DT.Point);
    if (!pointModel) {
      throw new Error('Point model is undefined');
    }
    const point = pointModel.value.clone();

    const command: ICommand = this._variableDistanceCommandsFactory.createRemoveVariableDistanceCommand(
      currentView,
      point,
      pointViewModel,
      distanceViewModel,
      this._leafletAlgoHelper
    );
    await this._commandProcessor.execute(command);
  }

  public async cancelIndication(): Promise<void> {
    if (!this._viewModels || !this._descriptor) {
      console.warn('ViewModels have to be defined');
      return;
    }
    this._removeViewModels(this._viewModels);
    await this._leafletAlgoHelper.deleteMeasurementAsync(this._descriptor);
    this._cleanup();
  }
}
